
import { faClock } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cs from 'classnames';
import React from 'react';
import { useMedia } from 'react-media';
import { bsClasses } from 'shared/bootstrap';
import { CancelApplicationButton } from '../../chrome/CancelApplication';
import { LoadingLabel } from '../../../shared/components/Loading';
import { ErrorBoundary } from 'shared/components/ErrorBoundary';
import { PageContainer } from 'shared/components/PageContainer';
import { RentalPageTitleHelmet } from 'rental/components/PageTitleHelmet';
import { SubmitActions } from '../../../shared/components/SubmitActions';
import { PageHeading } from 'shared/components/typography';
import { App_PortalFragment, ImportantInfoConditionsFragment, ImportantInfo_ApplicationFragment, useImportantInfoQuery, useSetImportantInfoReadMutation } from '../../generated.graphql';
import { GRID_MEDIA_QUERIES } from 'shared/media';
import { RentalStepProps } from 'rental/app/steps';
import { ImportantInfoSection } from './ImportantInfoSection';
import { useSections } from './ImportantInfoSections';


interface Props extends RentalStepProps {
  application: ImportantInfo_ApplicationFragment;
  portal: App_PortalFragment
}


export const ImportantInfo = (props: Props) => {
  const [result,] = useImportantInfoQuery({requestPolicy: "network-only", variables: {portalSlug: props.portal.slug}})
  const app = result.data?.portal?.myApplication;
  const residentStatus = result.data?.residentStatusEnum?.enumValues

  if (app && residentStatus) {
    return <ImportantInfoContent 
      {...props} 
      app={app} 
      residentStatusChoices={residentStatus.map(e=> ({
        value: e.name, 
        name: e.description || 'untitled'
      })) || []}
    />
  }
  return <LoadingLabel />
}

export const ImportantInfoContent = (props: Props & {app: ImportantInfoConditionsFragment, residentStatusChoices: {value: string, name: string}[]}) => {
  
  const sectionProps = {
    ...props.app.importantInfo,
    residentStatus: props.app.residentStatus || null, 
    residentStatusChoices: props.residentStatusChoices,
    portalSlug: props.portal.slug,
    items: props.app.items || [],
  }
  const sections = useSections(sectionProps);
  const [selected, setSelected] = React.useState<number | null>(null);
  const [completed, setCompleted] = React.useState<number | null>(props.application.importantInfoRead ? sections.length - 1 : null);
  const [mutationResult, mutate] = useSetImportantInfoReadMutation();
  const isXS = !useMedia({query: GRID_MEDIA_QUERIES.smGTE});

  // Use this instead of setSelected, so that the mutation is also called
  const updateSelected = (index: number | null) => {
    setSelected(index);
    if (props.application.importantInfoRead === null) {
      if (index && (index + 1 >= sections.length)) {
        mutate({input: {portalSlug: props.application.portal.slug}});
      }
    }
  }

  const toggleExpand = (index: number) => {
    updateSelected(index === selected ? null : index);
    if (!completed || index > completed) setCompleted(index);
  }

  const canExpand = (index: number) => {
    if (index === 0) return true;
    if (completed === null) 
      return false;
    return index <= completed + 1 && (sections[index - 1]?.isValid?.() ?? true);
  }

  const handleNextClick = () => {
    const isValid = (sections[completed ?? 0]?.isValid?.() ?? true);
    sections[completed ?? 0]?.setTouched?.(true);
    if (!isValid) {
      updateSelected(completed ?? 0);
      return;
    };

    if (completed && (completed + 1 >= sections.length)) {
      props.onSubmitSuccessful();
    }
    const plusOne = (completed === null ? -1 : completed) + 1
    if (plusOne < sections.length) {
      setCompleted(plusOne)
      updateSelected(plusOne)
      // event.preventDefault()
    }
  }

  const headingText = (<>The&nbsp;application should&nbsp;take&nbsp;less than&nbsp;5&nbsp;mins to&nbsp;complete.</>);


  return (
    <div>
      <RentalPageTitleHelmet>Important Information</RentalPageTitleHelmet>
      <div className="pt-3 mb-3">
        <div className={cs("d-flex align-items-center", bsClasses.container)}>
          <FontAwesomeIcon icon={faClock} size={isXS ? "3x" : "4x"} className={cs("d-md-none", isXS ? "mr-3" : "mr-4")} />
          {isXS ? (
            <p className="lead mb-0">{headingText}</p>
          ) : (
            <PageHeading className="mb-0 pb-0">{headingText}</PageHeading>
          )}
        </div>
      </div>
      <PageContainer>
        <p className="font-weight-normal mb-5">Please read through this important information before we get started...</p>
        <ul className="list-group">
          {sections.map((s, index) => 
            <ImportantInfoSection 
              key={index}
              title={s.title} 
              expanded={index === selected}
              checked={index <= (completed === null ? -1 : completed) && (sections[index]?.isValid?.() ?? true)}
              isInvalid={index <= (completed === null ? -1 : completed) && !(sections[index]?.isValid?.() ?? true)}
              onExpand={canExpand(index) ? () => toggleExpand(index) : undefined}
            >
              <ErrorBoundary title="ImportantInfoSection">
                {s.children}
              </ErrorBoundary>
            </ImportantInfoSection>
          )}
        </ul>
        <SubmitActions 
          actions={props.actions} 
          centre={<CancelApplicationButton portalSlug={props.portal.slug} />}
          submit={() => handleNextClick()}
          disabled={mutationResult.fetching}
        />

      </PageContainer>
    </div>
  )
}
